'use client';
import React, { useState } from 'react';
import { ACHIEVEMENTS } from '@/lib/mockData';

const CATEGORIES = [
  { id: 'cat-all', label: 'All', value: 'all' },
  { id: 'cat-messages', label: '💬 Messages', value: 'Messages' },
  { id: 'cat-consistency', label: '🔥 Consistency', value: 'Consistency' },
  { id: 'cat-community', label: '👥 Community', value: 'Community' },
  { id: 'cat-growth', label: '🚀 Growth', value: 'Growth' },
  { id: 'cat-milestones', label: '💎 Milestones', value: 'Milestones' },
];

const rarityConfig: Record<string, { color: string; bg: string; glow: string; label: string }> = {
  legendary: {
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.1)',
    glow: 'rgba(245,158,11,0.15)',
    label: '✨ Legendary',
  },
  epic: {
    color: '#a855f7',
    bg: 'rgba(168,85,247,0.1)',
    glow: 'rgba(168,85,247,0.1)',
    label: '⚡ Epic',
  },
  rare: {
    color: '#06b6d4',
    bg: 'rgba(6,182,212,0.1)',
    glow: 'rgba(6,182,212,0.08)',
    label: '💠 Rare',
  },
  common: {
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.1)',
    glow: 'rgba(107,114,128,0.06)',
    label: '· Common',
  },
};

export default function AchievementGrid() {
  const [activeCategory, setActiveCategory] = useState('all');

  // Backend integration point: fetch /api/achievements?category=activeCategory
  const filtered = ACHIEVEMENTS.filter((a) => !a.featured).filter(
    (a) => activeCategory === 'all' || a.category === activeCategory
  );

  return (
    <div>
      {/* Category Filters */}
      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.value)}
            className="px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 hover:scale-105"
            style={{
              background: activeCategory === cat.value ? 'var(--primary)' : 'var(--muted)',
              color: activeCategory === cat.value ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
              border: `1px solid ${activeCategory === cat.value ? 'var(--primary)' : 'var(--border)'}`,
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <span className="text-5xl">🏆</span>
          <p className="text-lg font-bold" style={{ color: 'var(--muted-foreground)' }}>
            No achievements in this category yet
          </p>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
            Keep being active — achievements unlock automatically.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
          {filtered.map((ach) => {
            const rarity = rarityConfig[ach.rarity];
            return (
              <div
                key={`ach-card-${ach.id}`}
                className="relative rounded-2xl overflow-hidden p-5 transition-all duration-300 hover:scale-[1.03] cursor-pointer group"
                style={{
                  background: 'var(--card)',
                  border: `1px solid ${rarity.color}25`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 30px ${rarity.glow}, 0 0 60px ${rarity.glow}50`;
                  (e.currentTarget as HTMLDivElement).style.borderColor = `${rarity.color}50`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                  (e.currentTarget as HTMLDivElement).style.borderColor = `${rarity.color}25`;
                }}
              >
                {/* Rarity glow bg */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse at top, ${rarity.glow} 0%, transparent 70%)`,
                  }}
                />

                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                      style={{ background: rarity.bg, border: `1px solid ${rarity.color}30` }}
                    >
                      {ach.icon}
                    </div>
                    <span
                      className="text-xs font-bold px-2.5 py-1 rounded-full"
                      style={{ background: rarity.bg, color: rarity.color }}
                    >
                      {rarity.label}
                    </span>
                  </div>

                  {/* Achievement name */}
                  <h3 className="text-base font-extrabold mb-1" style={{ color: 'var(--foreground)' }}>
                    {ach.title}
                  </h3>
                  <p className="text-xs leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
                    {ach.description}
                  </p>

                  {/* Key stat */}
                  <div
                    className="flex items-center justify-between p-3 rounded-xl mb-4"
                    style={{ background: rarity.bg, border: `1px solid ${rarity.color}20` }}
                  >
                    <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                      {ach.statLabel}
                    </span>
                    <span className="text-base font-extrabold text-tabular" style={{ color: rarity.color }}>
                      {ach.stat}
                    </span>
                  </div>

                  {/* User info */}
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: ach.avatarColor, color: '#fff' }}
                    >
                      {ach.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate" style={{ color: 'var(--foreground)' }}>
                        {ach.displayName}
                      </p>
                      <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                        {ach.username}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                        {ach.unlockedAt}
                      </p>
                    </div>
                  </div>

                  {/* Category badge */}
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${rarity.color}15` }}>
                    <span
                      className="text-xs font-semibold px-2 py-1 rounded-md"
                      style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                    >
                      {ach.category}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}